import React, { useEffect, useState, useRef } from 'react';
import { Container, Row, Col, Card, Badge, Spinner, Modal, Button, Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import SignatureCanvas from 'react-signature-canvas';
import Swal from 'sweetalert2';
import DOMPurify from 'isomorphic-dompurify';
import { 
  fetchAccidentReports, 
  submitParentSignature,
  selectReports, 
  selectMeta, 
  selectLoading, 
  selectError,
  selectSubmitLoading 
} from '../../../store/accidentReport/accidentReportSlice';
import { handleImageUpload } from '../../../api/uploads';

const AccidentReports = () => {
  const dispatch = useDispatch();
  const reports = useSelector(selectReports);
  const meta = useSelector(selectMeta);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const submitLoading = useSelector(selectSubmitLoading);

  const [currentPage, setCurrentPage] = useState(1);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showAddReportModal, setShowAddReportModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [signatureDate, setSignatureDate] = useState('');
  // submitLoading (Redux) only flips true once the submitParentSignature
  // thunk dispatches - it doesn't cover the signature image upload to AWS
  // that happens first, so the button showed no loader (and stayed
  // clickable) during that upload. This local flag covers the whole flow,
  // starting the instant validation passes.
  const [isSubmittingSignature, setIsSubmittingSignature] = useState(false);
  const signaturePadRef = useRef(null);
  const staffSignaturePadRef = useRef(null);
  const supervisorSignaturePadRef = useRef(null);

  // Form data for new report
  const [reportForm, setReportForm] = useState({
    incident_date: '',
    incident_time: '',
    location: '',
    incident_description: '',
    first_aid: '',
    follow_up_information: '',
    parent_contacted: false,
    parent_contacted_time: '',
    staff_name: '',
    staff_position: ''
  });

  useEffect(() => {
    dispatch(fetchAccidentReports({ page: currentPage, limit: 10 }));
  }, [dispatch, currentPage]);

  const handleSignReport = (report) => {
    setSelectedReport(report);
    setSignatureDate('');
    setShowSignatureModal(true);
  };

  const handleCloseModal = () => {
    setShowSignatureModal(false);
    setSelectedReport(null);
    setSignatureDate('');
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  const handleClearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
    }
  };

  const handleSubmitSignature = async () => {
    if (isSubmittingSignature) {
      return;
    }

    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
      Swal.fire({
        icon: 'warning',
        title: 'Signature Required',
        text: 'Please provide your signature before submitting.'
      });
      return;
    }

    if (!signatureDate) {
      Swal.fire({
        icon: 'warning',
        title: 'Date Required',
        text: 'Please select a signature date.'
      });
      return;
    }

    setIsSubmittingSignature(true);

    try {
      // Get signature as data URL
      const signatureDataURL = signaturePadRef.current.toDataURL('image/png');
      
      // Convert data URL to Blob
      const response = await fetch(signatureDataURL);
      const blob = await response.blob();
      
      // Create a file from blob
      const timestamp = new Date().getTime();
      const fileName = `signature_${timestamp}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });
      
      // Upload to AWS
      const signatureUrl = await handleImageUpload(file, 'signatures/', 'image/png');

      // Submit signature
      await dispatch(submitParentSignature({
        reportId: selectedReport.id,
        parent_signature: signatureUrl,
        parent_signature_date: signatureDate
      })).unwrap();

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Signature submitted successfully!'
      });

      handleCloseModal();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error || 'Failed to submit signature'
      });
    } finally {
      setIsSubmittingSignature(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString;
  };

  return (
    <Container>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div className="header-title">
                <h4 className="card-title">Indoor/Outdoor Accident Reports</h4>
              </div>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center p-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : error ? (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              ) : reports.length === 0 ? (
                <div className="text-center p-5">
                  <p className="text-muted">No accident reports available</p>
                </div>
              ) : (
                <>
                  <Row>
                    {reports.map((report) => (
                      <Col lg="12" key={report.id} className="mb-3">
                        <Card className="shadow-sm">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-start mb-3 card-row-head">
                              <div>
                                <h5 className="mb-2">
                                  Incident Date: {formatDate(report.incident_date)}
                                  <Badge bg="primary" className="ms-2">
                                    {formatTime(report.incident_time)}
                                  </Badge>
                                </h5>
                                <Badge bg={report.parent_signature ? 'success' : 'warning'}>
                                  {report.parent_signature ? 'Signed' : 'Pending Signature'}
                                </Badge>
                              </div>
                              {!report.parent_signature && (
                                <Button
                                  variant="primary"
                                  size="sm"
                                  className="d-flex align-items-center"
                                  onClick={() => handleSignReport(report)}
                                >
                                  <i className="icon material-symbols-outlined me-1" style={{ fontSize: '16px' }}>edit</i>
                                  Sign Report
                                </Button>
                              )}
                            </div>

                            <Row>
                              <Col md="6">
                                <p className="mb-2">
                                  <strong>Location:</strong> {report.location || 'N/A'}
                                </p>
                                <div className="mb-2">
                                  <strong>Incident Description:</strong>
                                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(report.incident_description) }} />
                                </div>
                                <div className="mb-2">
                                  <strong>First Aid:</strong>
                                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(report.first_aid) }} />
                                </div>
                                <div className="mb-2">
                                  <strong>Follow-up Information:</strong>
                                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(report.follow_up_information || 'N/A') }} />
                                </div>
                              </Col>
                              <Col md="6">
                                <p className="mb-2">
                                  <strong>Parent Contacted:</strong> {report.parent_contacted ? 'Yes' : 'No'}
                                  {report.parent_contacted && report.parent_contacted_time && (
                                    <span className="ms-2">at {formatTime(report.parent_contacted_time)}</span>
                                  )}
                                </p>
                                <p className="mb-2">
                                  <strong>Staff Name:</strong> {report.staff_name}
                                </p>
                                <p className="mb-2">
                                  <strong>Staff Position:</strong> {report.staff_position}
                                </p>
                                {report.staff_signature && (
                                  <p className="mb-2">
                                    <strong>Staff Signature:</strong>
                                    <br />
                                    <img 
                                      src={report.staff_signature} 
                                      alt="Staff Signature" 
                                      style={{ maxWidth: '200px', maxHeight: '100px', border: '1px solid #ddd', padding: '5px' }}
                                    />
                                  </p>
                                )}
                                {report.supervisor_signature && (
                                  <p className="mb-2">
                                    <strong>Supervisor Signature:</strong>
                                    <br />
                                    <img 
                                      src={report.supervisor_signature} 
                                      alt="Supervisor Signature" 
                                      style={{ maxWidth: '200px', maxHeight: '100px', border: '1px solid #ddd', padding: '5px' }}
                                    />
                                  </p>
                                )}
                                {report.parent_signature && (
                                  <div>
                                    <p className="mb-2">
                                      <strong>Parent Signature:</strong>
                                      <br />
                                      <img 
                                        src={report.parent_signature} 
                                        alt="Parent Signature" 
                                        style={{ maxWidth: '200px', maxHeight: '100px', border: '1px solid #ddd', padding: '5px' }}
                                      />
                                    </p>
                                    <p className="mb-2">
                                      <strong>Parent Signature Date:</strong> {formatDate(report.parent_signature_date)}
                                    </p>
                                  </div>
                                )}
                              </Col>
                            </Row>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>

                  {/* Pagination */}
                  {meta && meta.last_page > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                      <nav>
                        <ul className="pagination">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button 
                              className="page-link" 
                              onClick={() => setCurrentPage(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              Previous
                            </button>
                          </li>
                          {[...Array(meta.last_page)].map((_, index) => (
                            <li 
                              key={index + 1} 
                              className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                            >
                              <button 
                                className="page-link" 
                                onClick={() => setCurrentPage(index + 1)}
                              >
                                {index + 1}
                              </button>
                            </li>
                          ))}
                          <li className={`page-item ${currentPage === meta.last_page ? 'disabled' : ''}`}>
                            <button 
                              className="page-link" 
                              onClick={() => setCurrentPage(currentPage + 1)}
                              disabled={currentPage === meta.last_page}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Signature Modal */}
      <Modal 
        show={showSignatureModal} 
        onHide={handleCloseModal}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Sign Accident Report</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedReport && (
            <div className="mb-3">
              <p><strong>Incident Date:</strong> {formatDate(selectedReport.incident_date)}</p>
              <p><strong>Location:</strong> {selectedReport.location}</p>
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Parent Signature *</Form.Label>
            <div className="border rounded" style={{ 
              border: '2px solid #ddd', 
              backgroundColor: '#fff' 
            }}>
              <SignatureCanvas
                ref={signaturePadRef}
                canvasProps={{
                  className: 'signature-canvas',
                  style: { width: '100%', height: '200px' }
                }}
                backgroundColor="rgb(255, 255, 255)"
              />
            </div>
            <Button 
              variant="outline-secondary" 
              size="sm" 
              className="mt-2"
              onClick={handleClearSignature}
            >
              Clear Signature
            </Button>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Signature Date *</Form.Label>
            <Form.Control
              type="date"
              value={signatureDate}
              onChange={(e) => setSignatureDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal} disabled={isSubmittingSignature || submitLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitSignature}
            disabled={isSubmittingSignature || submitLoading}
          >
            {isSubmittingSignature || submitLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Submitting...
              </>
            ) : (
              'Submit Signature'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AccidentReports;
